import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { useGlobalSearch, type GlobalSearchResult } from '@/hooks/useGlobalSearch';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '@/lib/analytics';

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const { results, isLoading, hasResults, totalCount } = useGlobalSearch(query, {
    enabled: open,
  });

  // Keyboard shortcut listener (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Track search when results change
  useEffect(() => {
    if (query.length >= 2 && !isLoading) {
      track(ANALYTICS_EVENTS.SEARCH_PERFORMED, {
        search_type: 'global',
        search_query: query,
        result_count: totalCount,
        result_types: [
          results.jobs.length > 0 && 'job',
          results.rfqs.length > 0 && 'rfq',
          results.purchaseOrders.length > 0 && 'purchase_order',
          results.vendors.length > 0 && 'vendor',
        ].filter(Boolean),
      }, 'engagement');
    }
  }, [query, isLoading, totalCount, results, track]);

  // Handle result selection
  const handleSelect = useCallback((result: GlobalSearchResult) => {
    setOpen(false);
    setQuery('');
    navigate(result.href);
  }, [navigate]);

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Get status badge variant
  const getStatusVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (!status) return 'secondary';
    
    const statusLower = status.toLowerCase();
    if (['completed', 'paid', 'approved', 'active'].includes(statusLower)) {
      return 'default';
    }
    if (['cancelled', 'rejected', 'failed'].includes(statusLower)) {
      return 'destructive';
    }
    if (['pending', 'draft', 'open'].includes(statusLower)) {
      return 'outline';
    }
    return 'secondary';
  };

  return (
    <>
      {/* Search Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground 
                   bg-muted/50 border border-input rounded-md hover:bg-muted 
                   transition-colors w-full max-w-sm cursor-pointer"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left hidden sm:block">Search...</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border 
                        bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      {/* Search Dialog */}
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput 
          placeholder="Search jobs, RFQs, purchase orders, vendors..." 
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && query.length >= 2 && !hasResults && (
            <CommandEmpty>
              No results found for "{query}"
            </CommandEmpty>
          )}

          {!isLoading && query.length < 2 && query.length > 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search
            </div>
          )}

          {!isLoading && query.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Start typing to search...
            </div>
          )}

          {/* Jobs Results */}
          {!isLoading && results.jobs.length > 0 && (
            <CommandGroup heading={`Jobs (${results.jobs.length})`}>
              {results.jobs.map((result) => (
                <CommandItem
                  key={result.id}
                  value={`job-${result.id}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <result.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    {result.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{result.subtitle}</div>
                    )}
                  </div>
                  {result.status && (
                    <Badge variant={getStatusVariant(result.status)} className="text-xs shrink-0">
                      {result.status}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* RFQs Results */}
          {!isLoading && results.rfqs.length > 0 && (
            <CommandGroup heading={`RFQs (${results.rfqs.length})`}>
              {results.rfqs.map((result) => (
                <CommandItem
                  key={result.id}
                  value={`rfq-${result.id}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <result.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    {result.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{result.subtitle}</div>
                    )}
                  </div>
                  {result.status && (
                    <Badge variant={getStatusVariant(result.status)} className="text-xs shrink-0">
                      {result.status}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Purchase Orders Results */}
          {!isLoading && results.purchaseOrders.length > 0 && (
            <CommandGroup heading={`Purchase Orders (${results.purchaseOrders.length})`}>
              {results.purchaseOrders.map((result) => (
                <CommandItem
                  key={result.id}
                  value={`po-${result.id}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <result.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                    {result.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{result.subtitle}</div>
                    )}
                  </div>
                  {result.status && (
                    <Badge variant={getStatusVariant(result.status)} className="text-xs shrink-0">
                      {result.status}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Vendors Results */}
          {!isLoading && results.vendors.length > 0 && (
            <CommandGroup heading={`Vendors (${results.vendors.length})`}>
              {results.vendors.map((result) => (
                <CommandItem
                  key={result.id}
                  value={`vendor-${result.id}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <result.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{result.title}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
